<form
  class="create-project-form space-y-6"
  [formGroup]="projectFormModel.form"
  (ngSubmit)="submitForm()"
>
  <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
    <div class="create-project-panel space-y-5">
      <div>
        <label for="title-input" class="form-label required">
          {{ "INPUTS.TITLE" | translate }}
        </label>
        <input
          id="title-input"
          type="text"
          [placeholder]="'PLACEHOLDERS.PROJECT_TITLE' | translate"
          class="form-input"
          [class.is-invalid]="
            (titleFormControl?.touched || projectFormModel.form.dirty) &&
            titleFormControl?.errors
          "
          [class.is-valid]="!titleFormControl?.errors"
          [formControlName]="projectFormModel.fieldNames.title"
        />

        <div class="invalid-feedback">
          @if (titleFormControl?.errors?.["required"]) {
            <span>{{ "ERRORS.REQUIRED" | translate }}</span>
          }
          @if (titleFormControl?.errors?.["maxlength"]) {
            <span>
              {{
                "ERRORS.MAXLENGTH"
                  | translate
                    : {
                        requiredLength:
                          titleFormControl!.errors!["maxlength"]
                            .requiredLength,
                      }
              }}
            </span>
          }
        </div>
        <p class="create-project-hint">
          {{ "COMPONENTS.PROJECTS.CREATE_MODAL.TITLE_HINT" | translate }}
        </p>
      </div>

      <div>
        <label for="description-input" class="form-label">
          {{ "INPUTS.DESCRIPTION" | translate }}
        </label>
        <textarea
          id="description-input"
          class="form-input form-textarea"
          [class.is-invalid]="
            descriptionFormControl?.touched && descriptionFormControl?.errors
          "
          [class.is-valid]="
            descriptionFormControl?.dirty && !descriptionFormControl?.errors
          "
          [formControlName]="projectFormModel.fieldNames.description"
        ></textarea>

        <div class="invalid-feedback">
          @if (descriptionFormControl?.errors?.["maxlength"]) {
            <span>
              {{
                "ERRORS.MAXLENGTH"
                  | translate
                    : {
                        requiredLength:
                          descriptionFormControl!.errors!["maxlength"]
                            .requiredLength,
                      }
              }}
            </span>
          }
        </div>
        <p class="create-project-hint">
          {{ "COMPONENTS.PROJECTS.CREATE_MODAL.DESCRIPTION_HINT" | translate }}
        </p>
      </div>

      <div>
        <label for="organization-input" class="form-label">
          {{ "INPUTS.CUSTOMER" | translate }}
        </label>

        <app-search
          id="organization-input"
          customClass="form-search-input"
          [formControlName]="projectFormModel.fieldNames.organizationId"
          [items]="(companyOrganizations$ | async)!"
          [filterFn]="organizationSearchConfig.filterFn"
          [formatter]="organizationSearchConfig.formatter"
          [compareFn]="organizationSearchConfig.compareFn"
          [errorMessage]="'ERRORS.INCORRECT_SELECTED_ORGANIZATION' | translate"
        >
        </app-search>
      </div>
    </div>

    <div class="create-project-panel space-y-5">
      <div>
        @if (previewUrl$ | async; as previewUrl) {
          <div class="create-project-cover-block">
            <div class="create-project-cover-preview create-project-cover-preview--filled">
              <img [src]="previewUrl" [alt]="'INPUTS.PROJECT_LOGO' | translate" />
            </div>
            <div class="create-project-cover-actions">
              <app-file-upload
                [fileExtensions]="acceptedExtensions"
                [path]="projectImagePath"
                [prevExtension]="project.titleImageExtension"
                [fileTitle]="'title'"
                [showPreview]="false"
                (uploadedFileEvent)="onUploadFile($event)"
                (deletedFileEvent)="onDeleteFile()"
              />
              <button
                type="button"
                class="form-secondary-btn create-project-remove-cover-btn"
                (click)="onDeleteFile()"
              >
                {{ "BUTTONS.REMOVE" | translate }}
              </button>
            </div>
          </div>
        } @else {
          <div class="create-project-cover-block">
            <div class="create-project-cover-preview">
              <span>{{ "INPUTS.PROJECT_LOGO" | translate }}</span>
            </div>
            <div class="create-project-cover-actions">
              <app-file-upload
                [fileExtensions]="acceptedExtensions"
                [path]="projectImagePath"
                [prevExtension]="project.titleImageExtension"
                [fileTitle]="'title'"
                [showPreview]="false"
                (uploadedFileEvent)="onUploadFile($event)"
                (deletedFileEvent)="onDeleteFile()"
              />
            </div>
          </div>
        }
      </div>

      <div class="create-project-team-card">
        <div class="create-project-team-header">
          <p class="form-label">
            {{ "INPUTS.PERFORMER" | translate }}
          </p>
          @if (projectEmployees$ | async; as selectedEmployees) {
            <span class="create-project-team-count">
              {{
                "COMPONENTS.PROJECTS.CREATE_MODAL.SELECTED_TEAM"
                  | translate
                    : {
                        count: selectedEmployees.length,
                      }
              }}
            </span>
          }
        </div>

        <app-search
          customClass="form-search-input"
          [placeholderText]="'INPUTS.PERFORMER' | translate"
          [formControl]="selectedEmployeeControl"
          [items]="(companyEmployees$ | async)!"
          [filterFn]="employeeSearchConfig.filterFn"
          [formatter]="employeeSearchConfig.formatter"
          [compareFn]="employeeSearchConfig.compareFn"
          [errorMessage]="'ERRORS.INCORRECT_SELECTED_EMPLOYEE' | translate"
          (selectedItem)="onSelectEmployee($event)"
        >
        </app-search>

        <button
          type="button"
          class="form-secondary-btn create-project-add-btn"
          [disabled]="!selectedEmployee"
          (click)="addEmployee(selectedEmployee!)"
        >
          {{ "BUTTONS.ADD" | translate }}
        </button>

        <div class="create-project-team-list">
          @if (projectEmployees$ | async; as selectedEmployees) {
            @if (!selectedEmployees.length) {
              <p class="create-project-team-empty">
                {{ "MESSAGES.EMPTY_LIST" | translate: { param: ("TABLE_HEADERS.TEAM" | translate) } }}
              </p>
            } @else {
              @for (employeeProfile of selectedEmployees; track employeeProfile.id) {
                <div class="create-project-team-member">
                  <app-minimal-profile-info
                    [profile]="employeeProfile"
                    [fullNameClass]="'fw-bolder'"
                    [collapseDetailsOnMobile]="false"
                  />
                  <button
                    type="button"
                    class="close-btn"
                    [attr.aria-label]="'BUTTONS.CANCEL' | translate"
                    (click)="removeEmployee(employeeProfile)"
                  >
                  </button>
                </div>
              }
            }
          }
        </div>
      </div>
    </div>
  </div>

  <div class="create-project-actions">
    <button
      type="button"
      class="form-secondary-btn"
      (click)="onCancel()"
    >
      {{ "BUTTONS.CANCEL" | translate }}
    </button>
    <button
      type="submit"
      class="form-primary-btn"
      [disabled]="projectFormModel.form.invalid"
    >
      {{ "BUTTONS.SAVE_CHANGES" | translate }}
    </button>
  </div>
</form>
